import { Component, OnInit, OnDestroy, inject, input, output, signal, computed, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Subject, takeUntil } from 'rxjs';
import { ContactSelectorComponent } from '../entity-selection/contact-selector.component';
import { ContactFormComponent } from '../contact-form/contact-form.component';
import { ContactFormService } from '../../services/contact-form.service';
import { ContactResponseDto } from '@qc/shared';
import {
    ContactWithRole,
    ContactDialogMode,
    ContactSelectorConfig,
    ContactFormConfig,
} from '../../interfaces/contact.interface';

interface ContactSelectionResult {
    contact: ContactWithRole;
    roleDescription: string;
    context: string;
}

@Component({
    selector: 'qc-contact-selector-wrapper',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        InputTextModule,
        Button,
        DialogModule,
        ContactSelectorComponent,
        ContactFormComponent,
    ],
    template: `
        <div class="space-y-4">
            <div *ngIf="!useInlineMode() || inlineMode() === 'selector'">
                <qc-contact-selector
                    #contactSelector
                    [label]="selectorConfig().label || 'Contact'"
                    [disabled]="selectorConfig().disabled || false"
                    [required]="selectorConfig().required || false"
                    [showCreateButton]="selectorConfig().showCreateButton"
                    [deferSave]="true"
                    (contactSelected)="onContactSelected($event)"
                    (contactCreated)="onContactCreatedFromSelector($event)"
                    (validationChange)="onValidationChange($event)"
                />

                <div *ngIf="showCreateButton()" class="text-center mt-3">
                    <p-button
                        severity="secondary"
                        variant="outlined"
                        (click)="openCreateDialog()"
                        icon="pi pi-plus"
                        label="Add New Contact"
                        size="small"
                    />
                </div>
            </div>

            <div *ngIf="useInlineMode() && inlineMode() === 'create'" class="space-y-4">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-medium">Create New Contact</h3>
                    <p-button
                        severity="secondary"
                        variant="text"
                        (click)="cancelInlineMode()"
                        icon="pi pi-times"
                        size="small"
                    />
                </div>

                <qc-contact-form
                    [deferSave]="true"
                    [config]="contactFormConfig()"
                    (save)="onContactCreated($event)"
                    (cancel)="cancelInlineMode()"
                />
            </div>

            <div *ngIf="useInlineMode() && inlineMode() === 'confirm'" class="space-y-4">
                <div *ngIf="selectedContactForDialog()">
                    <div class="mb-4">
                        <div class="font-medium text-gray-900">{{ selectedContactForDialog()?.fullName }}</div>
                        <div class="text-sm text-gray-500">{{ selectedContactForDialog()?.email }}</div>
                    </div>

                    <div *ngIf="showRoleContextFields()" class="space-y-4">
                        <div>
                            <label class="block text-sm mb-1">Role Description</label>
                            <input
                                pInputText
                                [(ngModel)]="roleDescription"
                                placeholder="Enter role description"
                                class="w-full"
                            />
                        </div>

                        <div>
                            <label class="block text-sm mb-1">Context</label>
                            <input pInputText [(ngModel)]="context" placeholder="Enter context" class="w-full" />
                        </div>
                    </div>

                    <div class="flex justify-end space-x-2 mt-6">
                        <p-button
                            severity="secondary"
                            variant="outlined"
                            (click)="cancelInlineMode()"
                            label="Cancel"
                            size="small"
                        />
                        <p-button
                            severity="primary"
                            (click)="confirmSelection()"
                            label="Add Contact"
                            size="small"
                            [disabled]="!canConfirmSelection()"
                        />
                    </div>
                </div>
            </div>
        </div>

        <p-dialog
            *ngIf="!useInlineMode()"
            [(visible)]="showDialog"
            [header]="dialogMode() === ContactDialogMode.CREATE ? 'Create New Contact' : 'Contact Details'"
            [modal]="true"
            [closable]="true"
            styleClass="w-full max-w-md"
        >
            <div class="space-y-4">
                <div *ngIf="dialogMode() === ContactDialogMode.SEARCH && selectedContactForDialog()">
                    <div class="mb-4">
                        <div class="font-medium text-gray-900">{{ selectedContactForDialog()?.fullName }}</div>
                        <div class="text-sm text-gray-500">{{ selectedContactForDialog()?.email }}</div>
                    </div>
                </div>

                <qc-contact-form
                    *ngIf="dialogMode() === ContactDialogMode.CREATE"
                    [config]="contactFormConfig()"
                    (save)="onContactCreated($event)"
                    (cancel)="closeDialog()"
                />

                <div *ngIf="showRoleContextFields()" class="space-y-4">
                    <div>
                        <label class="block mb-2">Role Description</label>
                        <input
                            pInputText
                            [(ngModel)]="roleDescription"
                            placeholder="Enter role description"
                            class="w-full"
                        />
                    </div>

                    <div>
                        <label class="block mb-2">Context</label>
                        <input pInputText [(ngModel)]="context" placeholder="Enter context" class="w-full" />
                    </div>
                </div>
            </div>

            <div *ngIf="dialogMode() !== ContactDialogMode.CREATE" class="flex justify-end space-x-2 mt-6">
                <p-button severity="secondary" variant="outlined" (click)="closeDialog()" label="Cancel" size="small" />
                <p-button
                    severity="primary"
                    (click)="confirmSelection()"
                    label="Add Contact"
                    size="small"
                    [disabled]="!canConfirmSelection()"
                />
            </div>
        </p-dialog>
    `,
})
export class ContactSelectorWrapperComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    @ViewChild('contactSelector') contactSelector!: ContactSelectorComponent;

    selectorConfig = input<ContactSelectorConfig>({ showCreateButton: false });
    showCreateButton = input<boolean>(true);
    showRoleContextFields = input<boolean>(true);
    useInlineMode = input<boolean>(false);
    contactFormConfig = input<Partial<ContactFormConfig>>({});

    contactSelected = output<ContactSelectionResult>();
    contactCreated = output<ContactSelectionResult>(); 
    validationChange = output<boolean>();

    showDialog = signal<boolean>(false);
    dialogMode = signal<ContactDialogMode>(ContactDialogMode.SEARCH);
    selectedContactForDialog = signal<ContactResponseDto | null>(null);
    inlineMode = signal<'selector' | 'create' | 'confirm'>('selector');
    roleDescription = '';
    context = '';

    readonly ContactDialogMode = ContactDialogMode;

    ngOnInit(): void {
        this.resetFields();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onContactSelected(contact: ContactResponseDto): void {
        if (this.showRoleContextFields() && !this.useInlineMode()) {
            this.selectedContactForDialog.set(contact);
            this.dialogMode.set(ContactDialogMode.SEARCH);
            this.showDialog.set(true);
        } else {
            this.emitContactSelection(contact, '', '');
        }
    }

    onValidationChange(isValid: boolean): void {
        this.validationChange.emit(isValid);
    }

    openCreateDialog(): void {
        if (this.useInlineMode()) {
            this.inlineMode.set('create');
        } else {
            this.dialogMode.set(ContactDialogMode.CREATE);
            this.showDialog.set(true);
        }
        this.resetFields();
    }

    closeDialog(): void {
        this.showDialog.set(false);
        this.resetFields();
    }

    cancelInlineMode(): void {
        this.inlineMode.set('selector');
        this.resetFields();
    }

    onContactCreated(contact: ContactWithRole): void {
        if (this.useInlineMode()) {
            this.emitContactCreation(contact, contact.roleDescription || '', contact.context || '');
            this.inlineMode.set('selector');
        } else {
            this.closeDialog();
            if (this.showRoleContextFields()) {
                this.selectedContactForDialog.set(contact);
                this.dialogMode.set(ContactDialogMode.SEARCH);
                this.showDialog.set(true);
            } else {
                this.emitContactCreation(contact, contact.roleDescription || '', contact.context || '');
            }
        }
    }

    canConfirmSelection(): boolean {
        return !!this.selectedContactForDialog();
    }

    confirmSelection(): void {
        const contact = this.selectedContactForDialog();
        if (contact) {
            this.emitContactSelection(contact, this.roleDescription, this.context);
            if (this.useInlineMode()) {
                this.inlineMode.set('selector');
            } else {
                this.closeDialog();
            }
        }
    }

    private emitContactSelection(contact: ContactResponseDto, roleDescription: string, context: string): void {
        const contactWithRole: ContactWithRole = {
            ...contact,
            roleDescription,
            context,
        };

        this.contactSelected.emit({
            contact: contactWithRole,
            roleDescription,
            context,
        });
    }

    onContactCreatedFromSelector(contact: ContactResponseDto): void {
        if (this.showRoleContextFields()) {
            this.selectedContactForDialog.set(contact);
            this.dialogMode.set(ContactDialogMode.SEARCH);
            this.showDialog.set(true);
        } else {
            this.emitContactCreation(contact, '', '');
        }
    }

    private emitContactCreation(contact: ContactResponseDto, roleDescription: string, context: string): void {
        const contactWithRole: ContactWithRole = {
            ...contact,
            roleDescription,
            context,
        };

        this.contactCreated.emit({
            contact: contactWithRole,
            roleDescription,
            context,
        });
    }

    private resetFields(): void {
        this.selectedContactForDialog.set(null);
        this.roleDescription = '';
        this.context = '';
    }

    markAsTouched(): void {
        this.contactSelector?.markAsTouched();
    }
}
